import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useV2Auth } from '@/context/V2AuthContext';
import {
  FocusedJourneyLayout, MaskContainer, MaskContext, MaskHeadline,
  MaskInput, MaskTextarea, PrimaryAction, SecondaryAction, MaskBack, MaskClose, CharCounter,
} from '@/components/mask/MaskSystem';

/* Phase 2A — Carma 1:1 Sende-Journey im Mask System. Nutzt bestehende APIs. */

export default function GiveJourneyV2() {
  const { user, initialLoading, API_V2 } = useV2Auth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [recipient, setRecipient] = useState(null); // {type:'user'|'external', id?, name}
  const [text, setText] = useState('');
  const [senderName, setSenderName] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [shareLink, setShareLink] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    const pre = location.state?.preselectUser;
    if (pre) { setRecipient({ type: 'user', id: pre.id, name: pre.name }); setStep(2); }
    if (location.state?.prefillMessage) {
      setText(location.state.prefillMessage.slice(0, 10000));
      if (!pre) setStep(1);
    }
  }, [user, location.state]);

  const close = () => navigate(user ? '/inbox' : '/');

  const search = async (q) => {
    setQuery(q);
    if (!user || q.trim().length < 2) { setResults([]); return; }
    try {
      const res = await axios.get(`${API_V2}/users/search?query=${encodeURIComponent(q.trim())}`, { withCredentials: true });
      setResults(res.data.slice(0, 5));
    } catch (_e) { setResults([]); }
  };

  const choose = (r) => { setRecipient(r); setError(null); setStep(2); };

  const send = async () => {
    if (!text.trim() || sending) return;
    if (!user && recipient.type === 'external') { setError(null); setStep(4); return; }
    setSending(true); setError(null);
    try {
      if (recipient.type === 'user') {
        await axios.post(`${API_V2}/carma`, { receiverId: recipient.id, message: text.trim() }, { withCredentials: true });
        setStep(3);
      } else {
        await axios.post(`${API_V2}/carma/share`, { recipientName: recipient.name, message: text.trim() }, { withCredentials: true })
          .then((res) => { setShareLink(`${window.location.origin}${res.data.shareUrl}`); setStep(3); });
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('v2.common.error'));
    } finally { setSending(false); }
  };

  const sendGuest = async () => {
    if (!senderName.trim() || sending) return;
    setSending(true); setError(null);
    try {
      const res = await axios.post(`${API_V2}/carma/share-guest`, {
        recipientName: recipient.name,
        senderName: senderName.trim(),
        message: text.trim(),
      });
      setShareLink(`${window.location.origin}${res.data.shareUrl}`);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || t('v2.common.error'));
    } finally { setSending(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareLink); setCopied(true); setTimeout(() => setCopied(false), 2500); }
    catch (_e) { /* clipboard blocked */ }
  };

  const reset = () => { setStep(0); setQuery(''); setResults([]); setRecipient(null); setText(''); setSenderName(''); setShareLink(null); setError(null); };

  if (initialLoading) return null;
  const firstName = (recipient?.name || '').split(' ')[0];

  return (
    <FocusedJourneyLayout>
      <MaskClose onClick={close} data-testid="give-close-btn" />
      {((step > 0 && step < 3) || step === 4) && <MaskBack onClick={() => setStep(step === 4 ? 2 : step - 1)} data-testid="give-back-btn" />}

      {step === 0 && (
        <MaskContainer maskKey="g0" bgWord={t('v2.bgWords.wahrnehmen')} align="center" bgShift="-10%">
          <MaskContext>{t('v2.give.context0')}</MaskContext>
          <MaskHeadline>{t('v2.give.headline0')}</MaskHeadline>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-3">
            <PrimaryAction onClick={() => setStep(1)} data-testid="give-step0-primary">{t('v2.give.primary0')}</PrimaryAction>
            <SecondaryAction onClick={close} data-testid="give-step0-secondary">{t('v2.common.maybeLater')}</SecondaryAction>
          </div>
        </MaskContainer>
      )}

      {step === 1 && (
        <MaskContainer maskKey="g1" bgWord={t('v2.bgWords.du')} bgPosition="top" bgShift="18%">
          <MaskHeadline>{t('v2.give.headline1')}</MaskHeadline>
          <MaskInput
            autoFocus
            value={query}
            onChange={(e) => search(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && query.trim().length >= 2 && results.length === 0) choose({ type: 'external', name: query.trim() }); }}
            placeholder={t('v2.give.namePlaceholder')}
            data-testid="give-person-input"
          />
          <div className="mt-10 space-y-2">
            {results.map((u) => (
              <button key={u.id} type="button" onClick={() => choose({ type: 'user', id: u.id, name: u.name })} data-testid={`give-person-${u.id}`} className="mask-person-row block w-full text-left text-lg text-[var(--text)]/80 py-4 px-3 rounded-lg">
                {u.name}
              </button>
            ))}
            {query.trim().length >= 2 && (
              <button type="button" onClick={() => choose({ type: 'external', name: query.trim() })} data-testid="give-person-external" className="mask-person-row block w-full text-left py-4 px-3 rounded-lg">
                <span className="text-lg text-[var(--text)]/80">{query.trim()}</span>
                <span className="block text-sm text-[var(--muted)] mt-0.5">
                  {user ? t('v2.give.externalHintUser') : t('v2.give.externalHintGuest')}
                </span>
              </button>
            )}
          </div>
        </MaskContainer>
      )}

      {step === 2 && recipient && (
        <MaskContainer maskKey="g2" bgWord={t('v2.bgWords.aussprechen')} bgPosition="bottom" bgShift="-14%">
          <MaskContext>{t('v2.common.for', { name: recipient.name })}</MaskContext>
          <MaskHeadline>{t('v2.give.headline2', { name: firstName })}</MaskHeadline>
          <p className="mask-enter mask-enter-delay-2 text-sm text-[var(--muted)] mb-8" data-testid="give-subline2">{t('v2.give.subline2')}</p>
          <MaskTextarea
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 10000))}
            placeholder={t('v2.give.msgPlaceholder')}
            data-testid="give-message-textarea"
          />
          <CharCounter value={text} max={10000} />
          {error && <p className="text-sm text-red-700/80 mt-4" data-testid="give-error">{error}</p>}
          <div className="mt-8">
            <PrimaryAction onClick={send} disabled={!text.trim() || sending} data-testid="give-send-btn">
              {sending ? t('v2.common.loading') : t('v2.give.send')}
            </PrimaryAction>
          </div>
        </MaskContainer>
      )}

      {step === 4 && recipient && (
        <MaskContainer maskKey="g4" bgWord={t('v2.bgWords.vonDir')} bgPosition="top" bgShift="14%">
          <MaskContext>{t('v2.common.for', { name: recipient.name })}</MaskContext>
          <MaskHeadline>{t('v2.give.guestHeadline', { name: firstName })}</MaskHeadline>
          <MaskInput
            autoFocus
            value={senderName}
            onChange={(e) => setSenderName(e.target.value.slice(0, 60))}
            onKeyDown={(e) => { if (e.key === 'Enter') sendGuest(); }}
            placeholder={t('v2.give.yourName')}
            data-testid="give-sender-name-input"
          />
          {error && <p className="text-sm text-red-700/80 mt-4" data-testid="give-guest-error">{error}</p>}
          <div className="mt-10">
            <PrimaryAction onClick={sendGuest} disabled={!senderName.trim() || sending} data-testid="give-guest-send-btn">
              {sending ? t('v2.common.loading') : t('v2.give.send')}
            </PrimaryAction>
          </div>
        </MaskContainer>
      )}

      {step === 3 && (
        <MaskContainer maskKey="g3" bgWord={t('v2.bgWords.ausgesprochen')} align="center" bgShift="-8%">
          <MaskContext>{t('v2.common.for', { name: recipient?.name })}</MaskContext>
          {shareLink ? (
            <>
              <MaskHeadline>{t('v2.give.readyHeadline', { name: firstName })}</MaskHeadline>
              <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] leading-relaxed mb-10 max-w-md">
                {t('v2.give.readyText', { name: firstName })}
              </p>
              <div className="flex flex-col items-center gap-4">
                <PrimaryAction onClick={copyLink} data-testid="give-copy-link-btn">{copied ? t('v2.common.copied') : t('v2.give.copyLink')}</PrimaryAction>
                <div className="mask-enter mask-enter-delay-3 flex gap-6">
                  <a href={`https://wa.me/?text=${encodeURIComponent(`${t('v2.give.waText')}${shareLink}`)}`} target="_blank" rel="noreferrer" className="text-sm text-[var(--muted)] hover:text-[var(--text)] py-2" data-testid="give-share-whatsapp">{t('v2.common.perWhatsApp')}</a>
                  <a href={`mailto:?subject=${encodeURIComponent(t('v2.give.mailSubject'))}&body=${encodeURIComponent(shareLink)}`} className="text-sm text-[var(--muted)] hover:text-[var(--text)] py-2" data-testid="give-share-email">{t('v2.common.perEmail')}</a>
                </div>
              </div>
            </>
          ) : (
            <>
              <MaskHeadline>{t('v2.give.sentHeadline')}</MaskHeadline>
              <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] leading-relaxed mb-10 max-w-md">
                {t('v2.give.sentText', { name: firstName })}
              </p>
            </>
          )}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-3 mt-8">
            <SecondaryAction onClick={reset} data-testid="give-again-btn">{t('v2.give.again')}</SecondaryAction>
            <SecondaryAction onClick={close} data-testid="give-done-btn">{user ? t('v2.give.backToFuerdich') : t('v2.common.toHome')}</SecondaryAction>
          </div>
          {!user && shareLink && (
            <p className="mask-enter mask-enter-delay-3 text-sm text-[var(--muted)] mt-10 max-w-md mx-auto">
              {t('v2.give.guestAuthQuestion')}{' '}
              <button type="button" onClick={() => navigate('/auth')} data-testid="give-guest-auth-link" className="underline underline-offset-4 decoration-[var(--muted)]/40 hover:text-[var(--text)] transition-colors">
                {t('v2.give.guestAuthLink')}
              </button>
            </p>
          )}
        </MaskContainer>
      )}
    </FocusedJourneyLayout>
  );
}
